import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { apiError } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-bits";
import { Save, Loader2, AlertTriangle, Trash2, RefreshCw } from "lucide-react";

const NUM_FIELDS = [
  ["creator_percent", "Creator Income %"],
  ["platform_fee_percent", "Platform Fee %"],
  ["income_split_main", "Income Split — Main %"],
  ["income_split_topup", "Income Split — Top-Up %"],
  ["card_base_value", "Card Base Value $"],
  ["magic_card_earning_value", "Magic Card Earning $"],
  ["card_cooldown_hours", "Card Cooldown (hrs)"],
  ["matrix_width", "Matrix Width"],
  ["deposit_usage_percent", "Deposit Usage Limit %"],
  ["min_transfer", "Min Transfer $"],
  ["min_fund_request", "Min Fund Request $"],
  ["withdrawal_min_first", "Withdrawal Min (1st) $"],
  ["withdrawal_min_second", "Withdrawal Min (2nd) $"],
  ["withdrawal_multiple_third", "Withdrawal Multiple (3rd+) $"],
];

const field = "w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white outline-none focus:border-emerald-500/50 font-num";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resettingDefaults, setResettingDefaults] = useState(false);

  useEffect(() => { API.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader label="Loading business rules" />;

  const setNum = (k) => (e) => setS({ ...s, [k]: parseFloat(e.target.value) });
  const setPkg = (i, k) => (e) => {
    const packages = s.packages.map((p, idx) => idx === i ? { ...p, [k]: parseFloat(e.target.value) } : p);
    setS({ ...s, packages });
  };
  const setArr = (k) => (e) => setS({ ...s, [k]: e.target.value.split(",").map((v) => parseFloat(v.trim())) });

  const save = async () => {
    setBusy(true);
    try {
      const r = await API.put("/admin/settings", s);
      setS(r.data);
      toast.success("Business rules saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resetData = async () => {
    setResetting(true);
    try {
      const r = await API.post("/admin/reset-data", { confirm: confirmText.trim() });
      toast.success(r.data.message);
      setShowReset(false); setConfirmText("");
    } catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all business rules (packages, magic/silver cards, cooldowns, withdrawal tiers) to the latest built-in defaults? Your custom edits to these will be overwritten.")) return;
    setResettingDefaults(true);
    try {
      const r = await API.post("/admin/settings/reset-defaults");
      setS(r.data.settings);
      toast.success(r.data.message);
    } catch (e) { toast.error(apiError(e)); } finally { setResettingDefaults(false); }
  };

  return (
    <div>
      <PageHeader title="Business Rules" subtitle="Every business value from the specification — editable without code changes." testid="settings-header"
        right={
          <div className="flex items-center gap-2">
            <button data-testid="reset-defaults-btn" disabled={resettingDefaults} onClick={resetDefaults}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-cyan-500/40 text-white font-semibold px-4 py-2.5 rounded-xl disabled:opacity-60">
              {resettingDefaults ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Reset to Defaults
            </button>
            <button data-testid="save-settings" disabled={busy} onClick={save}
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 font-semibold px-5 py-2.5 rounded-xl">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
            </button>
          </div>
        }
      />

      <div className="glass rounded-2xl p-6 mb-6">
        <h3 className="font-display text-lg font-semibold text-white mb-4">Packages</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
              <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Amount $</th><th className="py-2 pr-4">Cap ×</th><th className="py-2 pr-4">Magic Cards</th><th className="py-2 pr-4">Silver Card $</th>
            </tr></thead>
            <tbody>
              {s.packages.map((p, i) => (
                <tr key={p.id} className="border-b border-white/5 last:border-0">
                  <td className="py-2 pr-4 text-white">{p.name}</td>
                  <td className="py-2 pr-4"><input data-testid={`pkg-${p.id}-amount`} type="number" className={`${field} w-24`} value={p.amount} onChange={setPkg(i, "amount")} /></td>
                  <td className="py-2 pr-4"><input type="number" className={`${field} w-20`} value={p.cap_multiplier} onChange={setPkg(i, "cap_multiplier")} /></td>
                  <td className="py-2 pr-4"><input type="number" className={`${field} w-20`} value={p.magic_cards} onChange={setPkg(i, "magic_cards")} /></td>
                  <td className="py-2 pr-4"><input type="number" className={`${field} w-20`} value={p.silver_card} onChange={setPkg(i, "silver_card")} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-4">Level Income</h3>
          <label className="text-xs text-slate-400">Level percentages (L1..L6, comma-separated)</label>
          <input data-testid="level-percents" className={`${field} mt-1 mb-4`} defaultValue={s.level_percents.join(", ")} onBlur={setArr("level_percents")} />
          <label className="text-xs text-slate-400">Qualification — direct joins per level (L1..L6)</label>
          <input data-testid="level-qualification" className={`${field} mt-1`} defaultValue={s.level_qualification.join(", ")} onBlur={setArr("level_qualification")} />
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-4">Matrix Level Slots</h3>
          <label className="text-xs text-slate-400">Slots per level (comma-separated)</label>
          <input data-testid="matrix-slots" className={`${field} mt-1`} defaultValue={s.matrix_level_slots.join(", ")} onBlur={setArr("matrix_level_slots")} />
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold text-white mb-4">Financial Rules</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {NUM_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs text-slate-400">{label}</label>
              <input data-testid={`setting-${k}`} type="number" step="0.01" className={`${field} mt-1`} value={s[k]} onChange={setNum(k)} />
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6 mt-6 border border-rose-500/30" data-testid="danger-zone">
        <h3 className="font-display text-lg font-semibold text-rose-300 flex items-center gap-2 mb-1">
          <AlertTriangle className="h-5 w-5" /> Danger Zone
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          Permanently erase ALL members and transactional data (deposits, transactions, withdrawals,
          magic &amp; silver cards, card drops, proof files). Your admin account and these business rules are kept;
          admin balances reset to $0. This cannot be undone.
        </p>
        <button data-testid="erase-data-btn" onClick={() => setShowReset(true)}
          className="inline-flex items-center gap-2 bg-rose-500/90 hover:bg-rose-500 text-white font-semibold px-5 py-2.5 rounded-xl">
          <Trash2 className="h-4 w-4" /> Erase All Data
        </button>
      </div>

      {showReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="reset-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => !resetting && setShowReset(false)} />
          <div className="relative glass rounded-2xl p-6 w-full max-w-md border border-rose-500/40">
            <h3 className="font-display text-lg font-semibold text-rose-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5" /> Confirm Data Erase
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              This will delete every member and all platform data on THIS environment. Type
              <span className="font-num text-rose-300 font-bold"> ERASE </span> below to confirm.
            </p>
            <input data-testid="reset-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type ERASE" className={`${field} mb-4 !font-display tracking-widest`} />
            <div className="flex gap-2">
              <button onClick={() => { setShowReset(false); setConfirmText(""); }} disabled={resetting}
                className="flex-1 border border-white/15 text-white py-2.5 rounded-xl">Cancel</button>
              <button data-testid="reset-confirm-btn" onClick={resetData} disabled={resetting || confirmText.trim() !== "ERASE"}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl">
                {resetting && <Loader2 className="h-4 w-4 animate-spin" />} Erase Everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
